/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import PropTypes from "prop-types";
import React from "react";
import { FormattedMessage } from "react-intl";
import { ContextMenuItemContainer } from "containers/contextMenu/ContextMenuItemContainer";
import { ContextMenuGroupItemContainer } from "containers/contextMenu/ContextMenuGroupItemContainer";
import ContextMenuSectionCss from "./css/ContextMenuSection.css";
import classNames from "classnames";

const ContextMenuSection = props => {
  const { menuItem, showTopBorder, showBottomBorder } = props;
  const { title, secondaryHeaderComponent, children, sectionId } = menuItem;

  const onContextMenu = eventObj => {
    if (eventObj.stopPropagation) {
      eventObj.stopPropagation();
      eventObj.preventDefault();
    }
  };

  const getChildren = () =>
    children.map((item, idx) => {
      if (item.children && item.children.length) return <ContextMenuGroupItemContainer key={idx} menuItem={item} />;
      return <ContextMenuItemContainer key={idx} menuItem={item} />;
    });

  return (
    <div
      data-testid="context-menu-section"
      onContextMenu={onContextMenu}
      className={classNames(
        ContextMenuSectionCss.container,
        showTopBorder ? ContextMenuSectionCss.topBorder : "",
        showBottomBorder ? ContextMenuSectionCss.bottomBorder : ""
      )}
    >
      <div className={ContextMenuSectionCss.sectionHeader}>
        <span id={sectionId} className={classNames(ContextMenuSectionCss.sectionTitle)}>
          <FormattedMessage id={title} />
        </span>
        {secondaryHeaderComponent}
      </div>
      {getChildren()}
    </div>
  );
};

ContextMenuSection.propTypes = {
  menuItem: PropTypes.shape({
    children: PropTypes.array.isRequired,
    secondaryHeaderComponent: PropTypes.elementType,
    title: PropTypes.string.isRequired
  }).isRequired
};

export { ContextMenuSection };
